/**
 * FilterBar.tsx — search + flag multiselect for the Bank Submission Status
 * page. Pure-controlled — the parent owns the search/flag state.
 */

import React from 'react';

import Ico from '../../components/Ico';
import { flagLabel } from '../../helpers/flag';
import type { Flag } from '../../type/api';

interface Props {
	search: string;
	onSearchChange: (s: string) => void;
	enabledFlags: Set<Flag>;
	onToggleFlag: (f: Flag) => void;
	autoRefresh: boolean;
	onToggleAutoRefresh: () => void;
}

const FLAGS: Flag[] = ['GREEN', 'AMBER', 'RED', 'GREY'];

export default function FilterBar({
	search,
	onSearchChange,
	enabledFlags,
	onToggleFlag,
	autoRefresh,
	onToggleAutoRefresh,
}: Props) {
	return (
		<div className="filterbar" role="search">
			<div className="search" style={{ flex: '1 1 320px', maxWidth: 420 }}>
				<Ico name="search" />
				<label htmlFor="bank-search" style={{ position: 'absolute', left: -9999 }}>
					Search banks
				</label>
				<input
					id="bank-search"
					type="search"
					placeholder="Search by bank name or ID…"
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
				/>
			</div>

			<div
				role="group"
				aria-label="Filter by status flag"
				style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
			>
				{FLAGS.map((f) => {
					const active = enabledFlags.has(f);
					return (
						<button
							key={f}
							type="button"
							className={`flag-toggle ${f.toLowerCase()}${active ? ' active' : ''}`}
							onClick={() => onToggleFlag(f)}
							aria-pressed={active}
						>
							<span className="dot" aria-hidden="true" />
							{flagLabel(f)}
						</button>
					);
				})}
			</div>

			<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
				<label
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 6,
						fontSize: 12,
						color: 'var(--text-dim)',
						cursor: 'pointer',
					}}
				>
					<input
						type="checkbox"
						checked={autoRefresh}
						onChange={onToggleAutoRefresh}
						aria-label="Auto-refresh every 60 seconds"
					/>
					Auto-refresh (60s)
				</label>
			</div>
		</div>
	);
}
